import React, { useState, useRef } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Paper, Chip } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import VerifiedIcon from '@mui/icons-material/Verified';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { API_BASE_URL } from 'src/apiConfig';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Animations 

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(21,101,192,0.25); }
  50%       { box-shadow: 0 0 0 10px rgba(21,101,192,0); }
`;


const PageRoot = styled(Box)({
  minHeight: '100vh',
  background: 'linear-gradient(160deg, #0d1b3e 0%, #1565c0 55%, #1976d2 100%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 16px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-30%',
    right: '-15%',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.04)',
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-20%',
    left: '-10%',
    width: '380px',
    height: '380px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.03)',
    pointerEvents: 'none',
  },
});

const Card = styled(Paper)({
  width: '100%',
  maxWidth: '540px',
  borderRadius: '24px',
  padding: '48px 44px',
  background: '#fff',
  boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
  position: 'relative',
  zIndex: 1,
  animation: `${fadeIn} 0.5s ease`,
});

const Logo = styled(Box)({
  width: '72px',
  height: '72px',
  borderRadius: '20px',
  background: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 24px',
  animation: `${pulse} 2.5s ease-in-out infinite`,
});

const StyledInput = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontSize: '15px',
    backgroundColor: '#f8faff',
    transition: 'box-shadow 0.2s',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
    '&.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(25,118,210,0.15)',
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#1976d2' },
});

const VerifyButton = styled(Button)({
  borderRadius: '12px',
  padding: '13px 0',
  fontWeight: 700,
  fontSize: '15px',
  textTransform: 'none',
  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
  boxShadow: '0 4px 16px rgba(21,101,192,0.35)',
  transition: 'transform 0.15s, box-shadow 0.15s',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 20px rgba(21,101,192,0.45)',
    background: 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)',
  },
  '&:active': { transform: 'translateY(0)' },
});

const ResultCard = styled(Box)(({ valid }) => ({
  marginTop: '28px',
  borderRadius: '16px',
  padding: '28px',
  background: valid === 'true'
    ? 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)'
    : 'linear-gradient(135deg, #fce4ec 0%, #fff3e0 100%)',
  border: `1.5px solid ${valid === 'true' ? '#a5d6a7' : '#ef9a9a'}`,
  animation: `${fadeIn} 0.4s ease`,
}));

const InfoRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '10px 0',
  borderBottom: '1px solid rgba(0,0,0,0.06)',
  '&:last-child': { borderBottom: 'none' },
});


const classLabels = {
  1: 'I OS', 2: 'II OS', 3: 'III OS', 4: 'IV OS',
  5: 'V OS', 6: 'VI OS', 7: 'VII OS', 8: 'VIII OS',
  9: 'I SS', 10: 'II SS', 11: 'III SS', 12: 'IV SS',
};

const awardColors = {
  'I': { bg: '#fff8e1', color: '#f57f17' },
  'II': { bg: '#e8f5e9', color: '#2e7d32' },
  'III': { bg: '#fce4ec', color: '#c62828' },
  'Похвала': { bg: '#e3f2fd', color: '#1565c0' },
  'Zlatna': { bg: '#fff8e1', color: '#f57f17' },
  'Zahvalnica': { bg: '#e8f5e9', color: '#2e7d32' },
};

const TYPE_LABEL_KEYS = {
  '1': 'diplomaTemplate.zlatna',
  '2': 'diplomaTemplate.prviRang',
  '3': 'diplomaTemplate.finalniRang',
  '4': 'diplomaTemplate.zahvalnica',
};


const getDiplomaTypeLabel = (templateType, typeLabel, t) => {
  const key = TYPE_LABEL_KEYS[String(templateType || '')];
  if (key) return t(key);

  if (!typeLabel) return t('general.unknown');
  const lower = String(typeLabel).trim().toLowerCase();
  if (/zlatna|golden|arany/.test(lower)) return t('diplomaTemplate.zlatna');
  if (/zahvalnica/.test(lower)) return t('diplomaTemplate.zahvalnica');
  if (/prvi|first.*rang|prvog/.test(lower)) return t('diplomaTemplate.prviRang');
  if (/final|финалн|finalni/.test(lower)) return t('diplomaTemplate.finalniRang');
  return typeLabel;
};


const DiplomaVerifyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [certId, setCertId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);

  const handleVerify = async () => {
    const trimmed = certId.trim();
    setTouched(true);
    if (!trimmed) return;

    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/diplomasVerify.php?id=${encodeURIComponent(trimmed)}`
      );
      const data = await res.json();
      setResult(data);
    } catch {
      setResult({ valid: false, error: t('diplomaVerifyPage.checkError') });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleVerify();
  };

  const handleReset = () => {
    setResult(null);
    setCertId('');
    setTouched(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const isSchoolLevel = result?.is_school_level || result?.is_zlatna || result?.is_zahvalnica;

  return (
    <PageRoot>
      <Box
        onClick={() => navigate(-1)}
        sx={{
          position: 'absolute',
          top: 24,
          left: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          zIndex: 5,
          color: '#fff',
          px: 1.5,
          py: 0.75,
          borderRadius: '10px',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.08)',
            transform: 'translateX(-2px)',
          },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 24 }} />
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '16px', letterSpacing: '0.2px' }}>
          {t('diplomaVerifyPage.back')}
        </Typography>
      </Box>

      <Card elevation={0}>
        <Logo>
          <WorkspacePremiumIcon sx={{ color: '#fff', fontSize: 36 }} />
        </Logo>

        <Typography
          variant="h5"
          fontWeight={800}
          textAlign="center"
          sx={{ color: '#0d1b3e', mb: 0.75, letterSpacing: '-0.5px' }}
        >
          {t('diplomaVerifyPage.title')}
        </Typography>
        <Typography
          variant="body2"
          textAlign="center"
          sx={{ color: '#607d8b', mb: 4, lineHeight: 1.6 }}
        >
          {t('diplomaVerifyPage.subtitle')}
        </Typography>

        {!result?.valid && (
          <>
            <StyledInput
              inputRef={inputRef}
              label={t('diplomaVerifyPage.certIdLabel')}
              value={certId}
              onChange={(e) => { setCertId(e.target.value); setTouched(false); }}
              onKeyDown={handleKeyDown}
              fullWidth
              error={touched && !certId.trim()}
              helperText={touched && !certId.trim() ? t('diplomaVerifyPage.certIdHelper') : ''}
              placeholder={t('diplomaVerifyPage.certIdPlaceholder')}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: '#90a4ae', mr: 1, fontSize: 20 }} />,
              }}
              sx={{ mb: 2 }}
            />
            <VerifyButton
              fullWidth
              variant="contained"
              onClick={handleVerify}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <VerifiedIcon />}
            >
              {loading ? t('diplomaVerifyPage.checking') : t('diplomaVerifyPage.verifyButton')}
            </VerifyButton>
          </>
        )}

        {result && (
          <ResultCard valid={result.valid ? 'true' : 'false'}>
            {result.valid ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <VerifiedIcon sx={{ color: '#2e7d32', fontSize: 32 }} />
                  <Box>
                    <Typography fontWeight={800} sx={{ color: '#1b5e20', fontSize: '16px' }}>
                      {t('diplomaVerifyPage.verifiedTitle')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#388e3c' }}>
                      {t('diplomaVerifyPage.verifiedId', { id: result.certification_id })}
                    </Typography>
                  </Box>
                </Box>

                {!isSchoolLevel && result.student_name && (
                  <InfoRow>
                    <EmojiEventsIcon sx={{ color: '#1976d2', fontSize: 20, mt: 0.2 }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', fontSize: '10px' }}>
                        {t('diplomaVerifyPage.student')}
                      </Typography>
                      <Typography fontWeight={700} sx={{ color: '#0d1b3e' }}>
                        {result.student_name}
                      </Typography>
                    </Box>
                  </InfoRow>
                )}

                {!isSchoolLevel && result.class && (
                  <InfoRow>
                    <SchoolIcon sx={{ color: '#1976d2', fontSize: 20, mt: 0.2 }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', fontSize: '10px' }}>
                        {t('diplomaVerifyPage.class')}
                      </Typography>
                      <Typography fontWeight={700} sx={{ color: '#0d1b3e' }}>
                        {classLabels[result.class] || `Разред ${result.class}`}
                      </Typography>
                    </Box>
                  </InfoRow>
                )}

                <InfoRow>
                  <SchoolIcon sx={{ color: '#1976d2', fontSize: 20, mt: 0.2 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', fontSize: '10px' }}>
                      {t('diplomaVerifyPage.school')}
                    </Typography>
                    <Typography fontWeight={700} sx={{ color: '#0d1b3e' }}>
                      {result.school_name}
                    </Typography>
                  </Box>
                </InfoRow>

                <InfoRow>
                  <CalendarTodayIcon sx={{ color: '#1976d2', fontSize: 20, mt: 0.2 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', fontSize: '10px' }}>
                      {t('diplomaVerifyPage.year')}
                    </Typography>
                    <Typography fontWeight={700} sx={{ color: '#0d1b3e' }}>
                      {result.diploma_year}
                    </Typography>
                  </Box>
                </InfoRow>

                <InfoRow>
                  <WorkspacePremiumIcon sx={{ color: '#1976d2', fontSize: 20, mt: 0.2 }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', fontSize: '10px' }}>
                      {t('diplomaVerifyPage.diplomaType')}
                    </Typography>
                    <Typography fontWeight={700} sx={{ color: '#0d1b3e' }}>
                      {getDiplomaTypeLabel(result.template_type, result.type_label, t)}
                    </Typography>
                  </Box>
                </InfoRow>

                {!isSchoolLevel && result.award && (
                  <InfoRow>
                    <EmojiEventsIcon sx={{ color: '#1976d2', fontSize: 20, mt: 0.2 }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', fontSize: '10px' }}>
                        {t('diplomaVerifyPage.award')}
                      </Typography>
                      <Chip
                        label={result.award}
                        size="small"
                        sx={{
                          mt: 0.5,
                          backgroundColor: awardColors[result.award]?.bg || '#f5f5f5',
                          color: awardColors[result.award]?.color || '#555',
                          fontWeight: 700,
                          fontSize: '13px',
                        }}
                      />
                    </Box>
                  </InfoRow>
                )}

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleReset}
                  sx={{
                    mt: 2.5,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    borderColor: '#a5d6a7',
                    color: '#2e7d32',
                    '&:hover': { backgroundColor: '#e8f5e9', borderColor: '#81c784' },
                  }}
                >
                  {t('diplomaVerifyPage.verifyAnother')}
                </Button>
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <ErrorOutlineIcon sx={{ color: '#c62828', fontSize: 32 }} />
                  <Box>
                    <Typography fontWeight={800} sx={{ color: '#b71c1c', fontSize: '16px' }}>
                      {t('diplomaVerifyPage.notFoundTitle')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#e53935' }}>
                      {t('diplomaVerifyPage.notFoundMessage')}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleReset}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 700,
                    borderColor: '#ef9a9a',
                    color: '#c62828',
                    '&:hover': { backgroundColor: '#fce4ec', borderColor: '#e57373' },
                  }}
                >
                  {t('diplomaVerifyPage.tryAgain')}
                </Button>
              </>
            )}
          </ResultCard>
        )}

        <Typography
          variant="caption"
          display="block"
          textAlign="center"
          sx={{ mt: 3, color: '#b0bec5' }}
        >
          {t('diplomaVerifyPage.footer')}
        </Typography>
      </Card>
    </PageRoot>
  );
};

export default DiplomaVerifyPage;